import axios from 'axios';
import { authorizedAxios } from './authorizedAxios';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  confidence: number;
}

export interface ClinicalInsights {
  symptoms: string[];
  medications: string[];
  diagnoses: string[];
  procedures: string[];
  labValues: string[];
  followUpRequired: boolean;
  followUpNotes?: string;
  riskFactors: string[];
  recommendations: string[];
}

export interface MedicalEntity {
  text: string;
  type: string;
  confidence: number;
  startIndex: number;
  endIndex: number;
  normalizedForm?: string;
}

export interface QualityMetrics {
  overallConfidence: number;
  medicalTermAccuracy: number;
  contextRelevance: number;
  completeness: number;
}

// Enhanced interfaces for unified transcript system
export interface SpeakerProfile {
  speakerId: string;
  role: 'physician' | 'patient' | 'nurse' | 'family' | 'other';
  confidence: number;
  segmentCount: number;
  totalSpeakingTime: number;
  segments: number[];
}

export interface SpeakerAnalysis {
  totalSpeakers: number;
  speakerProfiles: SpeakerProfile[];
  speakerTransitions: number;
  overlappingSpeech: number;
  dominantSpeaker: string;
  conversationDynamics: {
    patientEngagement: number;
    physicianListening: number;
    interruptionCount: number;
  };
}

export interface ContentClassification {
  totalSegments: number;
  clinicalSegments: TranscriptSegment[];
  administrativeSegments: TranscriptSegment[];
  socialSegments: TranscriptSegment[];
  proceduralSegments: TranscriptSegment[];
  medicalTermsCount: number;
  phiElementsCount: number;
  clinicalContentRatio: number;
  keyMedicalTopics: string[];
  consultationPhase: string;
}

export interface ClinicalIntelligence {
  structuredFindings: {
    chiefComplaint: string | null;
    historyPresentIllness: any;
    pastMedicalHistory: any;
    socialHistory: any;
    familyHistory: any;
    reviewOfSystems: any;
    physicalExamination: any;
    assessment: string | null;
    plan: string | null;
  };
  clinicalDecisionSupport: {
    differentialDiagnosis: any[];
    redFlags: string[];
    recommendedActions: string[];
    urgencyLevel: string;
  };
  documentationQuality: {
    completenessScore: number;
    missingElements: string[];
    suggestions: string[];
  };
  codingSuggestions: any;
}

export interface AgentExecution {
  agentName: string;
  version: string;
  executionTime: number;
  success: boolean;
  confidence: number;
  warnings: string[];
  errors: string[];
}

export interface AgentProcessing {
  pipelineVersion: string;
  processingDate: string;
  totalProcessingTime: number;
  agentsExecuted: AgentExecution[];
  overallConfidence: number;
  requiresReview: boolean;
  reviewPriority: string;
}

export interface EnhancedQualityMetrics extends QualityMetrics {
  speakerIdentificationAccuracy: number;
  contentClassificationAccuracy: number;
  clinicalExtractionCompleteness: number;
  overallQualityScore: number;
}

export interface Transcript {
  id: string;
  appointmentId: string;
  content: string;
  metadata: {
    model: string;
    confidence: number;
    duration: number;
    wordCount: number;
    language: string;
    processingTime: number;
  };
  segments?: TranscriptSegment[];
  clinicalInsights?: ClinicalInsights;
  /** @deprecated Use clinicalSummary sections instead */
  medicalEntities?: MedicalEntity[];
  contextType?: string;
  qualityMetrics?: EnhancedQualityMetrics;
  
  // NEW: AI Processing Results from Unified System
  speakerAnalysis?: SpeakerAnalysis;
  contentClassification?: ContentClassification;
  enhancedSegments?: any[];
  clinicalIntelligence?: ClinicalIntelligence;
  agentProcessing?: AgentProcessing;
  workflowGenerated?: boolean;
  workflowId?: string;
  
  version: number;
  createdAt: string;
  updatedAt: string;
}

class TranscriptionService {
  private baseUrl = API_BASE_URL;

  /**
   * Get transcript for an appointment
   */
  async getTranscriptByAppointment(appointmentId: string): Promise<Transcript> {
    try {
      const response = await authorizedAxios.get(
        `${this.baseUrl}/v1/transcription/appointment/${appointmentId}`,
        { 
          headers: getAuthHeader(),
          timeout: 15000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to get transcript');
      }

      return response.data.data;
    } catch (error) {
      console.error('Failed to get transcript:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error getting transcript');
      }
      throw error;
    }
  }

  /**
   * Delete transcript for an appointment
   */
  async deleteTranscript(appointmentId: string): Promise<void> {
    try {
      const response = await authorizedAxios.delete(
        `${this.baseUrl}/v1/transcription/appointment/${appointmentId}`,
        { 
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete transcript');
      }
    } catch (error) {
      console.error('Failed to delete transcript:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error deleting transcript');
      }
      throw error;
    }
  }
}

export const transcriptionService = new TranscriptionService();